import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Link from '@mui/material/Link';
import Toolbar from '@mui/material/Toolbar';
import { useLayoutEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { ROLES } from '@/auth/roles';
import { useAuth } from '@/auth/useAuth';
import { useViewingAs } from '@/auth/useViewingAs';

import { NAV_SECTIONS, STAFF_SECTIONS } from '../navigation';

import { AccountRail } from './AccountRail';
import { SideNav } from './SideNav';
import { TopBar } from './TopBar';
import { useNavFolded } from './useNavFolded';
import { ViewingBanner } from './ViewingBanner';

const NAV_WIDTH = 260;
/** Wide enough for an icon and its padding, and no more. */
const FOLDED_WIDTH = 72;
/** The account rail's width, from the wireframe. */
const RAIL_WIDTH = 200;

/**
 * The frame every portal screen sits inside: top bar, left navigation and the
 * page itself.
 *
 * The navigation is one component rendered into two drawers - a permanent one
 * at desktop width and a temporary one below it - so there is only ever one
 * navigation to keep correct.
 */
export function PortalLayout() {
  const [navOpen, setNavOpen] = useState(false);
  const [navFolded, toggleNavFolded] = useNavFolded();
  const deskWidth = navFolded ? FOLDED_WIDTH : NAV_WIDTH;
  const { user } = useAuth();

  // The organization name, and the switcher for anybody with more than one,
  // live in OrganizationSwitcher. It reads the signed-in person directly,
  // because switching changes their role as well as the label.

  // Activist staff manage accounts rather than belonging to one, so they get a
  // different set of sections entirely.
  const isStaff = user?.role === ROLES.ACTIVIST_STAFF;
  // Staff viewing an organization see its sections, as its own people do.
  const { viewing } = useViewingAs();
  const staffSections = isStaff && !viewing;
  const sections = staffSections ? STAFF_SECTIONS : NAV_SECTIONS;
  const navLabel = staffSections ? 'Staff sections' : 'Sections';

  /*
   * The top bar is fixed, and while staff view an organization it carries the
   * banner above it, so its height varies: taller with the banner, taller
   * again when the banner's words wrap on a narrow screen. It is measured, on
   * whichever bar element is on the page, through a callback ref.
   *
   * The two side columns start below it rather than running underneath it.
   * They used to start at the top of the window with blank space to push their
   * first entry down; once a tall open section made a column scroll, its first
   * entries slid up behind the bar and the banner.
   */
  const [barNode, setBarNode] = useState<HTMLElement | null>(null);
  const [barHeight, setBarHeight] = useState(0);
  // Measured before the browser paints, so the page never draws once under
  // the bar and then jumps.
  useLayoutEffect(() => {
    if (!barNode) return;
    const measure = () => setBarHeight(barNode.offsetHeight);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(barNode);
    return () => observer.disconnect();
  }, [barNode]);

  /** Where a permanent side column begins, and how tall it is. */
  const belowBar = { top: `${barHeight}px`, height: `calc(100% - ${barHeight}px)` };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* First in the tab order, before the navigation. That is the point of it. */}
      <Link
        href="#main"
        sx={{
          position: 'absolute',
          left: -9999,
          top: 0,
          zIndex: (t) => t.zIndex.tooltip,
          '&:focus': {
            left: 8,
            top: 8,
            px: 2,
            py: 1,
            bgcolor: 'background.paper',
            borderRadius: 1,
            boxShadow: 3,
          },
        }}
      >
        Skip to main content
      </Link>

      <TopBar
        onOpenNav={() => setNavOpen(true)}
        onToggleFold={toggleNavFolded}
        navFolded={navFolded}
        banner={<ViewingBanner />}
        ref={setBarNode}
      />

      {/* Narrow screens: hidden until the menu button opens it. MUI returns
          focus to that button when it closes. */}
      <Drawer
        variant="temporary"
        open={navOpen}
        onClose={() => setNavOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: NAV_WIDTH, boxSizing: 'border-box' },
        }}
      >
        <Toolbar />
        <SideNav
          sections={sections}
          label={navLabel}
          onNavigate={() => setNavOpen(false)}
        />
      </Drawer>

      {/* Desktop: always visible, part of the layout. */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: deskWidth,
          flexShrink: 0,
          transition: (t) => t.transitions.create('width'),
          '& .MuiDrawer-paper': {
            width: deskWidth,
            ...belowBar,
            overflowX: 'hidden',
            transition: (t) => t.transitions.create('width'),
            boxSizing: 'border-box',
            borderRight: 1,
            borderColor: 'divider',
          },
        }}
        open
      >
        <SideNav sections={sections} label={navLabel} folded={navFolded} />
      </Drawer>

      <Box component="main" id="main" sx={{ flexGrow: 1, p: 3, minWidth: 0 }}>
        {/* Sits under the fixed top bar, whatever its height. */}
        <Box sx={{ height: `${barHeight}px` }} />
        <Outlet />
      </Box>

      {/* The account rail, on a wide screen only: below it the same entries
          are in the avatar menu. After the page, so the keyboard reaches the
          page first. Not for staff in their own area: they belong to no
          organization, so it would hold Log Out alone, which the avatar menu
          already has. While viewing an organization they get its rail. */}
      {staffSections ? null : (
        <Drawer
          variant="permanent"
          anchor="right"
          sx={{
            display: { xs: 'none', lg: 'block' },
            width: RAIL_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: RAIL_WIDTH,
              ...belowBar,
              boxSizing: 'border-box',
              borderLeft: 1,
              borderColor: 'divider',
            },
          }}
          open
        >
          <AccountRail />
        </Drawer>
      )}
    </Box>
  );
}
